import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supplierModel } from '../../../models/supplierModel';
import { useSupplierAuth } from '../../../context/SupplierAuthContext';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faDownload, faUpload } from '@fortawesome/free-solid-svg-icons';
import '../../../styles/colors.css';

function SupplierOrderDetails() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { supplier } = useSupplierAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deliveryFile, setDeliveryFile] = useState(null);

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await supplierModel.getOrder(orderId);
      
      if (response.status === 1 || response.data) {
        const orderData = response.data?.order || response.order;
        setOrder(orderData);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type and size
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      const maxSize = 10 * 1024 * 1024; // 10MB

      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select a PDF or image file');
        return;
      }

      if (file.size > maxSize) {
        toast.error('File size must be less than 10MB');
        return;
      }

      setDeliveryFile(file);
    }
  };

  const handleUploadDelivery = async () => {
    if (!deliveryFile) {
      toast.error('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('delivery_document', deliveryFile);
      formData.append('order_id', order.requisition_id);
      formData.append('supplier_code', String(supplier?.supplier_user_supplier_id || supplier?.BP_Code || ''));

      const response = await supplierModel.uploadDocument(formData);
      
      if (response.status === 1) {
        toast.success('Delivery document uploaded successfully');
        setDeliveryFile(null);
        // Refresh order details
        fetchOrderDetails();
      } else {
        toast.error(response.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading delivery document:', error);
      toast.error('Failed to upload delivery document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadPO = async () => {
    try {
      const filePath = `${order.quotation_directory}/${order.requisition_po}`;
      const fileName = order.requisition_po;
      
      await supplierModel.downloadFile(filePath, fileName);
      toast.success('Download started');
    } catch (error) {
      console.error('Error downloading PO:', error);
      toast.error('Failed to download purchase order');
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(parseFloat(amount));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      return new Date(dateString).toLocaleDateString('en-GB');
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="container py-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container py-4">
        <div className="text-center">
          <h4>Order not found</h4>
          <p>The requested order could not be found or you don't have permission to view it.</p>
          <button className="btn btn-primary" onClick={() => navigate('/supplier/orders')}>
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      {/* Header */}
      <div className="d-flex align-items-center mb-4">
        <button 
          className="btn btn-outline-secondary me-3"
          onClick={() => navigate('/supplier/orders')}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Orders
        </button>
        <h2 className="mb-0">Order Details</h2>
      </div>

      {/* Order Information */}
      <div className="row">
        <div className="col-lg-8">
          <div className="card mb-4">
            <div className="card-header">
              <h5>Order Information</h5>
            </div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-sm-4">
                  <strong>Project Name:</strong>
                </div>
                <div className="col-sm-8">
                  {order.specification_title}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-4">
                  <strong>Specification ID:</strong>
                </div>
                <div className="col-sm-8">
                  {order.specification_id}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-4">
                  <strong>Quotation ID:</strong>
                </div>
                <div className="col-sm-8">
                  {order.quotation_id}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-4">
                  <strong>Purchase Order:</strong>
                </div>
                <div className="col-sm-8">
                  {order.requisition_po ? (
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={handleDownloadPO}
                    >
                      <FontAwesomeIcon icon={faDownload} className="me-1" />
                      Download PO
                    </button>
                  ) : (
                    'Not available'
                  )}
                </div>
              </div>

              {order.quotation_amount && (
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Order Amount:</strong>
                  </div>
                  <div className="col-sm-8">
                    {formatCurrency(order.quotation_amount)}
                  </div>
                </div>
              )}

              {order.requisition_po_note && (
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Notes:</strong>
                  </div>
                  <div className="col-sm-8">
                    <div className="alert alert-info">
                      {order.requisition_po_note}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Specification Details */}
          {order.specification_description && (
            <div className="card mb-4">
              <div className="card-header">
                <h5>Specification Details</h5>
              </div>
              <div className="card-body">
                <div dangerouslySetInnerHTML={{ __html: order.specification_description }} />
              </div>
            </div>
          )}
        </div>

        <div className="col-lg-4">
          {/* Delivery Documentation */}
          <div className="card mb-4">
            <div className="card-header">
              <h5>Delivery Documentation</h5>
            </div>
            <div className="card-body">
              <p className="text-muted small mb-3">
                Upload delivery notes, invoices, or other relevant documents for this order.
              </p>

              <div className="mb-3">
                <label htmlFor="deliveryFile" className="form-label">
                  Select Document
                </label>
                <input
                  type="file"
                  className="form-control"
                  id="deliveryFile"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                />
                <div className="form-text">
                  Accepted formats: PDF, JPG, PNG (max 10MB)
                </div>
              </div>

              {deliveryFile && (
                <div className="alert alert-info">
                  <strong>Selected file:</strong><br />
                  {deliveryFile.name}
                </div>
              )}

              <button
                type="button"
                className="btn btn-primary w-100"
                onClick={handleUploadDelivery}
                disabled={!deliveryFile || uploading}
              >
                {uploading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faUpload} className="me-2" />
                    Upload Document
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Order Status */}
          <div className="card">
            <div className="card-header">
              <h5>Order Status</h5>
            </div>
            <div className="card-body">
              <div className="text-center">
                <div className="mb-3">
                  <span className="badge bg-success fs-6">
                    {order.requisition_status || 'Active'}
                  </span>
                </div>
                
                {order.quotation_time && (
                  <p className="text-muted small">
                    Quotation submitted: {formatDate(order.quotation_time)}
                  </p>
                )}
                
                {order.requisition_po_date && (
                  <p className="text-muted small">
                    PO issued: {formatDate(order.requisition_po_date)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SupplierOrderDetails;
